import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { UserContext } from "../../utils/userContext";
import { isSuperAdmin } from "../../utils/roles";
import PageHeader from "../../components/ui/PageHeader";
import PageWrapper from "../../components/ui/PageWrapper";
import StatusBadge from "../../components/ui/StatusBadge";
import AdminSearchableList from "../../components/admin/AdminSearchableList";
import { useBackNavigation } from "../../navigation/BackNavigationContext";

export default function OrganisationsListPage() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { markAsRoot } = useBackNavigation();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isSuperAdmin(user)) {
      navigate("/admin/customers", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    markAsRoot(true);
  }, [markAsRoot]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get("/admin/organisations", {
        params: { search: debounced, page, limit },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError("Failed to load departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin(user)) fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, page, user]);

  const activeOrgId = user?.activeOrganisationId || user?.activeCompanyId;
  const totalPages = Math.ceil(total / limit);

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (o) => (
        <span className="text-blue-600 font-medium">
          {o.name}
          {String(o._id) === String(activeOrgId) && (
            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
              Current
            </span>
          )}
        </span>
      ),
    },
    { key: "slug", label: "Slug" },
    {
      key: "customer",
      label: "Customer",
      render: (o) => o.customerId?.name || "—",
    },
    {
      key: "status",
      label: "Status",
      render: (o) => <StatusBadge status={o.status || "active"} />,
    },
    {
      key: "memberCount",
      label: "Members",
      render: (o) => o.memberCount ?? "—",
    },
  ];

  return (
    <>
      <PageHeader
        title="Departments"
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e.target.value);
            setPage(1);
          },
          placeholder: "Search by name or slug",
          onClear: () => {
            setSearch("");
            setPage(1);
          },
        }}
      />
      <PageWrapper>
        <AdminSearchableList
          items={items}
          loading={loading}
          error={error}
          columns={columns}
          onRowClick={(o) => {
            const cid = o.customerId?._id || o.customerId;
            navigate(`/admin/customers/${cid}/organisations/${o._id}`);
          }}
          emptyMessage="No departments found."
          pagination={{
            page,
            totalPages,
            total,
            onPrev: () => setPage((p) => Math.max(1, p - 1)),
            onNext: () => setPage((p) => p + 1),
          }}
        />
      </PageWrapper>
    </>
  );
}
